import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";

import {
  Integration,
  IntegrationBilling,
  listIntegrations,
  setIntegrationEnabled,
} from "@/api/integrations";
import { Switch } from "@/components/form";
import { IntegrationForm } from "@/components/IntegrationForm";
import { ErrorBox, Spinner } from "@/components/Spinner";
import { Page, PageHeader, StatusPill, type Tone } from "@/components/ui";

/**
 * Services tiers communs — les comptes fournisseurs partagés par tous les
 * clients. Un environnement peut en redéfinir un depuis sa propre fiche.
 *
 * Cette page ne connaît AUCUN fournisseur. Elle affiche ce que le backend
 * décrit — nom du vendeur, objet, facturation, portée, interrupteur. Elle
 * tenait auparavant sa propre liste en dur, avec ses propres titres : deux
 * cartes sur quatre ne nommaient jamais leur fournisseur, et les libellés
 * divergeaient de ceux du référentiel des titres. Ajouter un fournisseur est
 * désormais une entrée dans le registre backend, et rien ici.
 */

const BILLING: Record<IntegrationBilling, { label: string; tone: Tone }> = {
  // Le ton d'avertissement est réservé à ce qui coûte à l'appel — c'est le même
  // code couleur que le référentiel des titres, et le seul axe qu'un opérateur
  // ne peut pas déduire de l'écran.
  metered: { label: "Billed per call", tone: "warning" },
  flat_rate: { label: "Flat rate", tone: "info" },
  free: { label: "Free", tone: "success" },
};

function EnabledSwitch({ integration }: { integration: Integration }) {
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (enabled: boolean) =>
      setIntegrationEnabled(token, integration.provider, enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["integrations"] });
    },
  });

  const on = integration.enabled;
  return (
    <div className="flex items-center gap-3">
      <Switch
        checked={on}
        label={`${on ? "Turn off" : "Turn on"} ${integration.vendor}`}
        disabled={mutation.isPending}
        onCheckedChange={(next) => mutation.mutate(next)}
      />
      <span className="text-sm text-ink-muted">
        {on ? "Queried" : "Off — the proxy no longer calls it"}
      </span>
      {mutation.error ? (
        <span className="text-sm text-danger-fg">
          {(mutation.error as Error).message}
        </span>
      ) : null}
    </div>
  );
}

function IntegrationCard({ integration }: { integration: Integration }) {
  const billing = BILLING[integration.billing];
  const off = integration.switchable && !integration.enabled;

  return (
    <div className={off ? "card-sunken" : "card"}>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 className="text-lg font-semibold text-ink">
          {integration.label}
        </h2>
        {/* Le vendeur, toujours nommé. C'est ce qui manquait. */}
        <span className="text-sm font-medium text-ink-muted">
          {integration.vendor}
        </span>
      </div>

      <div className="mt-2 flex flex-wrap gap-2">
        {/* Labels about the account, not states of it: no dot. */}
        {billing ? (
          <StatusPill tone={billing.tone} dot={false}>
            {billing.label}
          </StatusPill>
        ) : null}
        {integration.globalOnly ? (
          <StatusPill
            tone="neutral"
            dot={false}
            title="This account serves the control plane, which serves all customers."
          >
            Shared across all environments
          </StatusPill>
        ) : (
          <StatusPill
            tone="neutral"
            dot={false}
            title="An environment can override this account from its own settings."
          >
            Overridable per environment
          </StatusPill>
        )}
        {integration.source === "unset" ? (
          <StatusPill tone="neutral">Not configured</StatusPill>
        ) : null}
      </div>

      <p className="mb-4 mt-3 text-sm text-ink-muted">{integration.purpose}</p>

      {integration.switchable ? (
        <div className="mb-4 border-y border-line py-3">
          <EnabledSwitch integration={integration} />
        </div>
      ) : null}

      <IntegrationForm provider={integration.provider} />
    </div>
  );
}

export function IntegrationsPage() {
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";

  const { data, isLoading, error } = useQuery({
    queryKey: ["integrations"],
    queryFn: () => listIntegrations(token),
    enabled: Boolean(token),
  });

  return (
    <Page width="form">
      <PageHeader
        title="Integrations"
        subtitle="Third-party provider accounts used by default for all customers."
      />

      {isLoading ? <Spinner /> : null}
      {error ? <ErrorBox error={error as Error} /> : null}

      <div className="space-y-6">
        {data?.items.map((integration) => (
          <IntegrationCard key={integration.provider} integration={integration} />
        ))}
      </div>
    </Page>
  );
}
